// Gaming venture maths — become a gamer. Two moves a week: PRACTICE (spend energy,
// raise your skill, no cash) or COMPETE (pay a tournament entry, win a prize scaled
// by skill). The lesson: skill is an investment. Luck only nudges the result — a
// practised player wins reliably, a beginner usually loses the entry. Push your skill
// past the pro line and you "go pro": streaming pays you passively every week.

export type GamingAction = 'rest' | 'practice' | 'compete';

export const PRACTICE_ENERGY = 10; // energy a week of focused practice costs
export const TOURNAMENT_ENTRY = 20; // cost to enter a weekly tournament
export const PRO_SKILL = 60; // skill at which you can earn passively (go pro)
const STREAM_BASE = 8; // base weekly streaming income at the pro line

const clampSkill = (skill: number): number => Math.max(0, Math.min(100, skill));

/** Skill gained from a week of practice — fast early, slows near mastery
 *  (diminishing returns: the last 10% is the hardest). */
export function practiceGain(skill: number): number {
  return Math.max(2, Math.round(12 * (1 - clampSkill(skill) / 100)));
}

/** Tournament prize for a skill level and a luck draw in [0, 1).
 *  Skill sets the base payout; luck is a modest ±30% band on top. At low skill the
 *  base is below the entry (you usually lose); at high skill it's well above it. */
export function tournamentPrize(skill: number, luck: number): number {
  const s = clampSkill(skill) / 100;
  const base = TOURNAMENT_ENTRY * (0.5 + 2.5 * s); // 0.5×entry at skill 0 → 3×entry at skill 100
  const band = 0.7 + 0.6 * Math.max(0, Math.min(1, luck)); // [0.7, 1.3)
  return Math.max(0, Math.round(base * band));
}

/** Expected (luck-neutral) net result of one tournament at this skill — for previews. */
export function expectedTournamentNet(skill: number): number {
  return tournamentPrize(skill, 0.5) - TOURNAMENT_ENTRY;
}

/** Has the player mastered the game enough to earn passively (go pro)? */
export function isPro(skill: number): boolean {
  return clampSkill(skill) >= PRO_SKILL;
}

/** Passive weekly streaming income once pro — scales with skill above the pro line. */
export function streamIncome(skill: number): number {
  const s = clampSkill(skill);
  if (s < PRO_SKILL) return 0;
  return Math.round(STREAM_BASE + (s - PRO_SKILL) * 0.8);
}
